import {
  COMMISSION_STATUS_LABELS,
  formatEur,
  formatVatRate,
  type CommissionStatus,
  type CommissionSupplierView,
} from '@caterinx/shared-types';
import { Alert, AlertDescription, AlertTitle } from '@caterinx/ui/components/ui/alert';
import { Badge } from '@caterinx/ui/components/ui/badge';
import { Button } from '@caterinx/ui/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@caterinx/ui/components/ui/card';
import { Skeleton } from '@caterinx/ui/components/ui/skeleton';
import { useToast } from '@caterinx/ui/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { fetchInvoiceDownload, fetchMyCommissions } from '../lib/commissions.js';
import { formatDate } from '../lib/rfq.js';

/**
 * `/provisionen` — Provisionshistorie und Rechnungen des Betriebs (Phase 6).
 *
 * ### Warum diese Seite zum MVP gehört und kein Nachklapp ist
 *
 * caterinx rechnet mit dem Lieferanten ab, nicht mit dem Kunden (D10, D19). Damit ist diese
 * Seite die **einzige** Stelle, an der ein Betrieb die Geschäftsbeziehung nachvollziehen
 * kann: was offen ist, was bezahlt wurde, welche Rechnung zu welchem Auftrag gehört. Wer sie
 * weglässt, verlässt sich darauf, dass niemand seine E-Mails löscht.
 *
 * ### Der offene Betrag steht oben, nicht in einer Summenzeile unten
 *
 * „Was schulde ich gerade" ist die Frage, mit der jemand diese Seite öffnet. Die Zahl kommt
 * vom Server (`openGrossCents`) und wird hier **nicht** nachgerechnet — zwei Rechenwege für
 * dieselbe Zahl sind zwei Zahlen (Goldene Regel 3).
 *
 * ### Die Sperre steht dabei, wenn es sie gibt
 *
 * Ein Betrieb, der keine Anfragen mehr bekommt und nicht weiß warum, ruft nicht an — er hört
 * auf. Deshalb sagt der Hinweis oben ausdrücklich, was los ist und was ihn aufhebt.
 */

/** Nur diese vier Zustände können in einer Lieferantensicht überhaupt auftreten. */
const STATUS_VARIANT: Record<CommissionStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  accrued: 'secondary',
  invoiced: 'default',
  overdue: 'destructive',
  paid: 'outline',
  uncollectible: 'outline',
  void: 'outline',
};

export function CommissionsPage() {
  const { data, isPending, isError } = useQuery({
    queryKey: ['my-commissions'],
    queryFn: fetchMyCommissions,
  });

  const items = data?.items ?? [];
  const open = items.filter((item) => item.status === 'invoiced' || item.status === 'overdue');
  const rest = items.filter((item) => item.status !== 'invoiced' && item.status !== 'overdue');
  const hasOverdue = items.some((item) => item.status === 'overdue');

  return (
    <main className="mx-auto max-w-3xl space-y-6 p-4 sm:p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Provisionen</h1>
          <p className="text-muted-foreground text-sm">Ihre Vermittlungsprovisionen und Rechnungen</p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/">Zum Portal</Link>
        </Button>
      </header>

      {isPending && (
        <>
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-32 w-full" />
        </>
      )}

      {isError && (
        <Alert variant="destructive">
          <AlertTitle>Liste konnte nicht geladen werden</AlertTitle>
          <AlertDescription>Bitte laden Sie die Seite neu.</AlertDescription>
        </Alert>
      )}

      {data && (
        <Card>
          <CardHeader>
            <CardDescription>Offener Betrag</CardDescription>
            <CardTitle className="text-3xl">{formatEur(data.openGrossCents)}</CardTitle>
          </CardHeader>
          {data.openGrossCents > 0 && (
            <CardContent>
              <p className="text-muted-foreground text-sm">
                Gestellte Rechnungen, die noch offen sind — brutto, inklusive Umsatzsteuer. Zahlbar per Überweisung
                unter Angabe der Rechnungsnummer.
              </p>
            </CardContent>
          )}
        </Card>
      )}

      {hasOverdue && (
        <Alert variant="destructive">
          <AlertTitle>Eine Rechnung ist überfällig</AlertTitle>
          <AlertDescription>
            Solange eine Provisionsrechnung offen ist, erhalten Sie keine neuen Einladungen zu Ausschreibungen. Mit dem
            Zahlungseingang wird das automatisch wieder aufgehoben. Gibt es zu einer Rechnung eine Rückfrage, schreiben
            Sie uns — eine Klärung ist uns lieber als eine Mahnung.
          </AlertDescription>
        </Alert>
      )}

      {data && items.length === 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Noch keine Provision</CardTitle>
            <CardDescription>
              Eine Provision entsteht erst mit einem Zuschlag: Sobald ein Kunde Ihr Angebot annimmt, erscheint sie hier
              — zusammen mit der Rechnung, die Sie wenige Tage später per E-Mail erhalten.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {open.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold">Offen</h2>
          {open.map((item) => (
            <CommissionCard key={item.commissionId} commission={item} />
          ))}
        </section>
      )}

      {rest.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold">Abgeschlossen</h2>
          {rest.map((item) => (
            <CommissionCard key={item.commissionId} commission={item} />
          ))}
        </section>
      )}

      {items.length > 0 && (
        <p className="text-muted-foreground text-xs">
          Die Provision berechnet sich aus Ihrer Angebotssumme netto **bei Zuschlag**. Spätere Erhöhungen der
          Auftragssumme bleiben provisionsfrei. Wird eine Veranstaltung abgesagt, melden Sie das bitte binnen 14 Tagen —
          wir stornieren die Provision dann.
        </p>
      )}
    </main>
  );
}

/* --------------------------------------------------------------------------------------------
 * Eine Provision
 * ----------------------------------------------------------------------------------------- */

function CommissionCard({ commission }: { commission: CommissionSupplierView }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  /**
   * Die vorsignierte URL wird **beim Klick** geholt, nicht mit der Liste.
   *
   * Sie trägt keine Identität: Wer sie hat, kommt an die Datei. Zwanzig davon im Speicher
   * einer offenen Registerkarte wären zwanzig Zugänge zu Belegen mit Anschrift, USt-IdNr.
   * und Bankverbindung.
   */
  async function download() {
    setLoading(true);
    try {
      const { downloadUrl } = await fetchInvoiceDownload(commission.commissionId);
      window.location.assign(downloadUrl);
    } catch {
      toast({
        variant: 'destructive',
        title: 'Rechnung konnte nicht geöffnet werden',
        description: 'Bitte versuchen Sie es in einem Moment noch einmal.',
      });
    } finally {
      setLoading(false);
    }
  }

  const label = COMMISSION_STATUS_LABELS[commission.status];

  const variant = STATUS_VARIANT[commission.status];

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base">
              {commission.invoiceNumber ?? `Veranstaltung am ${formatDate(commission.eventDate)}`}
            </CardTitle>
            <CardDescription>
              {formatDate(commission.eventDate)}
              {commission.eventCity !== '' && ` · ${commission.eventCity}`}
            </CardDescription>
          </div>
          <Badge variant={variant}>{label}</Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2">
          <div>
            <p className="text-muted-foreground text-xs">Provision brutto</p>
            <p className="text-2xl font-semibold">{formatEur(commission.amount.grossCents)}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Netto</p>
            <p className="text-xl">{formatEur(commission.amount.netCents)}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Satz</p>
            <p className="text-xl">{formatVatRate(commission.commissionRateBp)}</p>
          </div>
        </div>

        <dl className="grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
          <Row label="Auftragssumme netto" value={formatEur(commission.offerNetCents)} />
          {commission.invoicedAt && (
            <Row label="Rechnungsdatum" value={formatDate(commission.invoicedAt.slice(0, 10))} />
          )}
          {commission.dueAt && <Row label="Zahlbar bis" value={formatDate(commission.dueAt.slice(0, 10))} />}
          {commission.paidAt && <Row label="Bezahlt am" value={formatDate(commission.paidAt.slice(0, 10))} />}
        </dl>

        {commission.status === 'accrued' && (
          <p className="text-muted-foreground text-sm">
            Die Rechnung dazu erhalten Sie in den nächsten Tagen per E-Mail.
          </p>
        )}

        {commission.hasInvoicePdf && (
          <Button variant="outline" onClick={() => void download()} disabled={loading}>
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            {loading ? 'Wird geöffnet …' : 'Rechnung als PDF'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
